import React, { useEffect, useState, useCallback } from "react";
import PlacementTestTable from "../../components/Tables/PlacementTestTable";
import { API } from "../../shared/api/Api";
import { useSelector } from "react-redux";
import { selectUserInformations } from "../../features/authSlice";
import { showToast } from "../../utils/utils";
import axios from "axios";

const DEBOUNCE_DELAY = 700;

const PlacementTestPage = () => {
  const user = useSelector(selectUserInformations);
  const [activeTab, setActiveTab] = useState(user?.student ? "My Tests" : "All Tests");
  const [reFetchData, setReFetchData] = useState(false);
  const [placementTests, setPlacementTests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loader, setLoader] = useState(true);
  const [orderBy, setOrderBy] = useState("created_at");
  const [orderDir, setOrderDir] = useState("DESC");

  useEffect(() => {
    if (user?.student) {
      setActiveTab("My Tests");
    }
  }, [user]);

  const fetchPlacementTests = useCallback(async () => {
    try {
      const endpoint = activeTab === "My Tests" 
        ? API.PlacementTests.myTests 
        : API.PlacementTests.all;

      const { data } = await axios.get(endpoint, {
        params: {
          page: currentPage,
          search: debouncedSearch,
          orderBy: orderBy,
          orderDir: orderDir,
        },
        headers: { Authorization: `Bearer ${user.token}` },
      });

      if (data.status === 200 && data.errorCode === 0) {
        const payload = data?.data;
        const items =
          (Array.isArray(payload) && payload) ||
          (Array.isArray(payload?.data) && payload.data) ||
          (Array.isArray(payload?.placement_tests) && payload.placement_tests) ||
          [];

        setPlacementTests(items);

        // Supports both old and new pagination shapes.
        const current = payload?.pagination?.current_page ?? payload?.current_page;
        const total = payload?.pagination?.total_pages ?? payload?.last_page;
        if (current) setCurrentPage(current);
        if (total) setTotalPages(total);
      } else {
        showToast("Error", "Failed to fetch placement tests", "error");
      }
    } catch (error) {
      showToast("Error", error.response?.data?.message || "An error occurred", "error");
    }
  }, [currentPage, debouncedSearch, orderBy, orderDir, user.token, activeTab]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, DEBOUNCE_DELAY);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const fetchData = async () => {
      setLoader(true);
      try {
        await fetchPlacementTests();
      } finally {
        setLoader(false);
        setReFetchData(false);
      }
    };
    fetchData();
  }, [activeTab, reFetchData, fetchPlacementTests]);

  const handleSort = (column, direction) => {
    setOrderBy(column);
    setOrderDir(direction);
  };

  return (
    <div className="min-h-screen p-2 sl:pt-0 sm:p-2 md:p-4 sl:p-6">
      <PlacementTestTable
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        placementTests={placementTests}
        currentPage={currentPage}
        totalPages={totalPages}
        onSearchChange={setSearch}
        onPageChange={setCurrentPage}
        loader={loader}
        setReFetchData={setReFetchData}
        onSort={handleSort}
        currentOrderBy={orderBy}
        currentOrderDir={orderDir}
      />
    </div>
  );
};

export default PlacementTestPage;
